// O ÚNICO ponto do portal que fala com a API (a plugchef.admin.api, em /admin/v1).
//
// Nada mais no projeto usa `fetch` diretamente: o tratamento de sessão expirada, o envio do token e
// o formato de erro existem num lugar só. Se um componente chamasse `fetch` por conta própria, a
// renovação de sessão não valeria para ele e a pessoa cairia no login no meio da navegação.
//
// MESMA ORIGEM (branch web): em produção o Caddy de admin.plugchef.com.br entrega o portal e repassa
// `/admin/v1/*` para a API; em dev o Vite faz o mesmo (vite.config.ts). Por isso a base é relativa e
// não há CORS. O refresh vive num cookie httpOnly `plugchef_admin_refresh` com `path=/admin/v1/auth`:
// o navegador o manda só para as rotas de auth, e o JavaScript nunca o lê.
import type { UsuarioAdmin } from './tipos';

const BASE = (import.meta.env.VITE_API_URL ?? '/admin/v1').replace(/\/+$/, '');

/** Formato padrão de erro das APIs PlugChef: `{ success:false, error, codigo?, dados? }`. */
export class ErroApi extends Error {
  constructor(
    public readonly status: number,
    mensagem: string,
    public readonly codigo?: string,
    public readonly dados?: unknown,
    /** A sessão acabou (401 sem renovação possível): quem trata é o store da sessão. */
    public readonly expirado = false,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

/** A resposta do login (etapa SESSAO), do `totp/ativar` e do `renovar`: o token novo é `acesso`. */
export interface DadosSessao {
  usuario: UsuarioAdmin;
  acesso: string;
}

type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH';

interface Opcoes {
  metodo?: Metodo;
  corpo?: unknown;
  /**
   * Rota pública (login, ativar o TOTP): não manda o token nem tenta renovar num 401. Sem isso, "senha
   * errada" (401 CREDENCIAIS_INVALIDAS) dispararia uma renovação e viraria "Sessão encerrada".
   */
  publica?: boolean;
  /** A resposta é um ARQUIVO (PDF), devolvido como Blob; o erro continua vindo no JSON padrão. */
  binario?: boolean;
  /** Uso interno: evita laço infinito de renovação. */
  jaRenovou?: boolean;
}

/**
 * Token de acesso em memória, nunca em localStorage: qualquer script injetado na página lê o
 * localStorage, e um token roubado dali vale até expirar (15 min). Em memória ele morre ao fechar a
 * aba — e a sessão continua porque o refresh vive no cookie httpOnly. O custo é recarregar a página
 * exigir uma renovação: um pedido a mais no início.
 */
let tokenAcesso: string | null = null;
let renovando: Promise<DadosSessao | null> | null = null;
const aoPerderSessao = new Set<() => void>();
const aoRenovar = new Set<(u: UsuarioAdmin) => void>();

/**
 * Renova o token de acesso pelo cookie de refresh (`POST /auth/renovar`).
 *
 * A promessa é COMPARTILHADA entre chamadas simultâneas de propósito: ao abrir uma tela, várias
 * consultas disparam juntas e todas recebem 401 ao mesmo tempo. Sem isso seriam várias renovações em
 * paralelo — e como o refresh é ROTACIONADO a cada uso, as perdedoras apresentariam um refresh já
 * trocado, que a API trata como ROUBO: derruba todas as sessões da pessoa (e o acesso já emitido cai na
 * hora). A pessoa seria expulsa justamente por abrir a tela.
 */
function renovar(): Promise<DadosSessao | null> {
  if (renovando) return renovando;

  renovando = (async () => {
    try {
      const r = await fetch(`${BASE}/auth/renovar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!r.ok) return null;
      const corpo = await r.json().catch(() => null);
      const dados = corpo?.data as DadosSessao | undefined;
      if (!dados || typeof dados.acesso !== 'string') return null;
      tokenAcesso = dados.acesso;
      aoRenovar.forEach((fn) => fn(dados.usuario));
      return dados;
    } catch {
      return null;
    } finally {
      // Zera só depois de resolver, para as chamadas em espera aproveitarem o mesmo resultado.
      setTimeout(() => {
        renovando = null;
      }, 0);
    }
  })();

  return renovando;
}

async function requisitar<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const cabecalhos: Record<string, string> = {};
  if (opcoes.corpo !== undefined) cabecalhos['Content-Type'] = 'application/json';
  if (tokenAcesso && !opcoes.publica) cabecalhos.Authorization = `Bearer ${tokenAcesso}`;

  let r: Response;
  try {
    r = await fetch(`${BASE}${caminho}`, {
      method: opcoes.metodo ?? 'GET',
      headers: cabecalhos,
      credentials: 'include',
      body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
    });
  } catch {
    throw new ErroApi(0, 'Não foi possível falar com o servidor. Confira a conexão e tente de novo.', 'SEM_CONEXAO');
  }

  if (r.status === 401 && !opcoes.publica && !opcoes.jaRenovou) {
    // Token expirado é o caso comum (15 minutos): renova e repete, sem a pessoa perceber.
    if (await renovar()) return requisitar<T>(caminho, { ...opcoes, jaRenovou: true });
    tokenAcesso = null;
    aoPerderSessao.forEach((fn) => fn());
    throw new ErroApi(401, 'Sua sessão terminou. Entre de novo.', 'SESSAO_INVALIDA', undefined, true);
  }

  if (opcoes.binario && r.ok) return (await r.blob()) as T;

  const corpo = await r.json().catch(() => null);

  if (!r.ok || corpo?.success === false) {
    const mensagem = typeof corpo?.error === 'string' && corpo.error ? corpo.error : mensagemPadrao(r.status);
    throw new ErroApi(r.status, mensagem, corpo?.codigo, corpo?.dados, r.status === 401 && !opcoes.publica);
  }

  return corpo?.data as T;
}

function comParametros(caminho: string, params?: object): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    // Só o que tem valor entra: `?antes=undefined` viraria a string "undefined" no servidor.
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return `${caminho}${s ? `?${s}` : ''}`;
}

function mensagemPadrao(status: number): string {
  if (status === 404) return 'Não encontrado.';
  if (status === 403) return 'Você não tem permissão para esta ação.';
  if (status === 429) return 'Muitas tentativas. Aguarde alguns minutos e tente de novo.';
  if (status >= 500) return 'O servidor não conseguiu responder agora. Tente de novo em instantes.';
  return `Erro ${status}`;
}

/** Mensagem para mostrar à pessoa a partir de qualquer erro. */
export function mensagemDeErro(e: unknown, padrao = 'Não foi possível concluir.'): string {
  if (e instanceof Error && e.message) return e.message;
  return padrao;
}

/** O cliente HTTP do portal (uma instância só: a da equipe da Resulti). */
export const http = {
  get: <T>(caminho: string, params?: object): Promise<T> => requisitar(comParametros(caminho, params)),
  post: <T>(caminho: string, corpo?: unknown): Promise<T> => requisitar(caminho, { metodo: 'POST', corpo: corpo ?? {} }),
  put: <T>(caminho: string, corpo?: unknown): Promise<T> => requisitar(caminho, { metodo: 'PUT', corpo: corpo ?? {} }),
  patch: <T>(caminho: string, corpo?: unknown): Promise<T> => requisitar(caminho, { metodo: 'PATCH', corpo: corpo ?? {} }),
  /** GET de um arquivo (PDF), com a sessão: o link direto não serve, porque o token não vai num <a>. */
  baixar: (caminho: string): Promise<Blob> => requisitar<Blob>(caminho, { binario: true }),
  /** POST a rota pública (login, ativar o TOTP): sem token e sem renovação. */
  publico: <T>(caminho: string, corpo: unknown): Promise<T> => requisitar(caminho, { metodo: 'POST', corpo, publica: true }),
  definirToken: (t: string | null): void => {
    tokenAcesso = t;
  },
  temToken: (): boolean => tokenAcesso !== null,
  /** Renova pelo cookie de refresh. Devolve a sessão nova ou `null` (sem cookie, vencido, revogado). */
  renovar,
  /** Encerra no servidor (`POST /auth/sair`, melhor esforço) e esquece o token local. */
  sair: async (): Promise<void> => {
    // Se a chamada falhar, a sessão local acaba de qualquer forma — deixar a pessoa presa numa tela
    // porque o servidor não respondeu seria pior.
    try {
      await fetch(`${BASE}/auth/sair`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch {
      /* sem rede: o cookie vence sozinho; o token local some abaixo */
    }
    tokenAcesso = null;
  },
  /** Avisa que a sessão acabou no meio do uso (401 e a renovação falhou). */
  assinarPerdaDeSessao: (fn: () => void): (() => void) => {
    aoPerderSessao.add(fn);
    return () => {
      aoPerderSessao.delete(fn);
    };
  },
  /** Avisa a cada renovação bem-sucedida — o nome ou o papel podem ter mudado no servidor. */
  assinarRenovacao: (fn: (u: UsuarioAdmin) => void): (() => void) => {
    aoRenovar.add(fn);
    return () => {
      aoRenovar.delete(fn);
    };
  },
};
